// Uma chamada por vez, fora de componente (sobrevive à navegação). A Meta não faz
// trickle ICE: o SDP só sai depois que a coleta de candidatos termina.
import WhatsappCallsAPI from 'dashboard/api/whatsappCalls';
import { useWhatsappCallsStore } from 'dashboard/stores/whatsappCalls';
import { startCopilot } from './copilot';

const ICE = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };

let pc = null;
let localStream = null;
let remoteStream = null;
let remoteAudio = null;
let copilot = null;

async function openMedia() {
  localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  pc = new RTCPeerConnection(ICE);
  localStream.getTracks().forEach(track => pc.addTrack(track, localStream));
  remoteStream = new MediaStream();
  remoteAudio = new Audio();
  remoteAudio.autoplay = true;
  pc.ontrack = event => {
    remoteStream.addTrack(event.track);
    remoteAudio.srcObject = remoteStream;
  };
}

function gathered() {
  const waitForCandidates = new Promise(resolve => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') resolve();
    });
  });
  const timeout = new Promise(resolve => {
    setTimeout(resolve, 3000);
  });
  return Promise.race([waitForCandidates, timeout]);
}

function teardown() {
  copilot?.stop();
  copilot = null;
  pc?.close();
  pc = null;
  localStream?.getTracks().forEach(track => track.stop());
  localStream = null;
  remoteStream = null;
  if (remoteAudio) remoteAudio.srcObject = null;
  remoteAudio = null;
  useWhatsappCallsStore().clearActive();
}

async function attachCopilot(token) {
  const store = useWhatsappCallsStore();
  try {
    copilot = await startCopilot({ token, localStream, remoteStream });
    if (!pc) {
      copilot?.stop();
      copilot = null;
    }
  } catch {
    store.setCopilotStatus('unavailable');
  }
}

export async function answerCall(incoming) {
  const store = useWhatsappCallsStore();
  const { data } = await WhatsappCallsAPI.show(
    incoming.conversationId,
    incoming.callId
  );
  await openMedia();
  try {
    await pc.setRemoteDescription({ type: 'offer', sdp: data.sdp });
    await pc.setLocalDescription(await pc.createAnswer());
    await gathered();
    const res = await WhatsappCallsAPI.accept(
      incoming.conversationId,
      incoming.callId,
      { sdp: pc.localDescription.sdp }
    );
    store.removeIncoming(incoming.callId);
    store.setActive({ ...incoming, copilotToken: res.data.copilot_token });
    store.markConnected();
    attachCopilot(res.data.copilot_token);
  } catch (error) {
    teardown();
    store.removeIncoming(incoming.callId);
    throw error;
  }
}

export async function startCall({ conversationId, contactName }) {
  const store = useWhatsappCallsStore();
  await openMedia();
  try {
    await pc.setLocalDescription(await pc.createOffer());
    await gathered();
    const { data } = await WhatsappCallsAPI.create(conversationId, {
      sdp: pc.localDescription.sdp,
    });
    store.setActive({
      callId: data.id,
      conversationId,
      contactName,
      direction: 'outbound',
      copilotToken: data.copilot_token,
    });
  } catch (error) {
    teardown();
    throw error;
  }
}

export async function outboundAnswered() {
  const store = useWhatsappCallsStore();
  const { callId, conversationId, copilotToken } = store.active;
  const { data } = await WhatsappCallsAPI.show(conversationId, callId);
  await pc.setRemoteDescription({ type: 'answer', sdp: data.sdp });
  store.markConnected();
  attachCopilot(copilotToken);
}

export async function rejectCall(incoming) {
  await WhatsappCallsAPI.reject(incoming.conversationId, incoming.callId);
  useWhatsappCallsStore().removeIncoming(incoming.callId);
}

export async function hangup() {
  const { active } = useWhatsappCallsStore();
  try {
    if (active)
      await WhatsappCallsAPI.terminate(active.conversationId, active.callId);
  } finally {
    teardown();
  }
}

export function toggleMute() {
  const store = useWhatsappCallsStore();
  const muted = !store.active.muted;
  localStream?.getAudioTracks().forEach(track => {
    track.enabled = !muted;
  });
  store.setMuted(muted);
}

export function remoteEnded() {
  teardown();
}
